import { getAuthToken } from '../utils/authUtils';
import { sanitizeTimestamps } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';



/**
 * Get messages between a provider and a patient
 * @param {string} patientId - The patient ID
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of messages to return
 * @param {string} options.before - Message ID to start after for pagination
 * @returns {Promise<Object>} Messages data
 */
export const getPatientMessages = async (patientId, facilityId, options = {}) => {
    try {
      const token = await getAuthToken();
      const { limit = 500, before } = options;
      
      const params = new URLSearchParams({
        patientId,
        facilityId,
        limit: limit.toString()
      });
      
      if (before) {
        params.append('before', before);
      }
  
      const response = await fetch(`${API_BASE_URL}/internal/messages/direct/patient?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      return sanitizeTimestamps(data);
    } catch (error) {
      console.error('Error fetching patient messages:', error);
      throw error;
    }
  };

/**
 * Get messages between a provider and a patient
 * @param {string} patientId - The patient ID
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of messages to return
 * @param {string} options.before - Message ID to start after for pagination
 * @returns {Promise<Object>} Messages data
 */
export const getPatientMessagesForProvider = async (patientId, providerId, facilityId, options = {}) => {
  try {
    const token = await getAuthToken();
    const { limit = 50, before } = options;
    
    const params = new URLSearchParams({
      patientId,
      providerId,
      facilityId,
      limit: limit.toString()
    });
    
    if (before) {
      params.append('before', before);
    }

    const response = await fetch(`${API_BASE_URL}/internal/messages/direct/user/${providerId}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error fetching patient messages:', error);
    throw error;
  }
};

/**
 * Send a message from provider to patient
 * @param {Object} messageData - The message data
 * @param {string} messageData.patientId - The patient ID
 * @param {string} messageData.providerId - The provider ID (current user)
 * @param {string} messageData.facilityId - The facility ID
 * @param {string} messageData.content - The message content
 * @param {string} messageData.type - Message type (default: 'text')
 * @param {string} messageData.direction - Message direction (default: 'provider_to_patient')
 * @returns {Promise<Object>} Created message data
 */
export const sendMessageToPatient = async (messageData) => {
  try {
    const token = await getAuthToken();
    
    const payload = {
      recipientId: messageData.recipientId,
      senderId: messageData.senderId,
      facilityId: messageData.facilityId,
      content: messageData.content,
      type: messageData.type || 'text',
      direction: messageData.direction || 'provider_to_patient'
    };

    const response = await fetch(`${API_BASE_URL}/internal/messages/direct`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error sending message to patient:', error);
    throw error;
  }
};

/**
 * Get all direct messages for a user (provider)
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of messages to return
 * @param {string} options.before - Message ID to start after for pagination
 * @returns {Promise<Object>} Messages data
 */
export const getAllDirectMessages = async (providerId, facilityId, options = {}) => {
  try {
    const token = await getAuthToken();
    const { limit = 50, before } = options;
    
    const params = new URLSearchParams({
      facilityId,
      limit: limit.toString()
    });
    
    if (before) {
      params.append('before', before);
    }

    const response = await fetch(`${API_BASE_URL}/internal/messages/direct?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error fetching all direct messages:', error);
    throw error;
  }
 };

/**
 * Get unread messages count for a provider
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @param {number} limit - Maximum number of unread messages to return
 * @returns {Promise<Object>} Unread messages data
 */
export const getUnreadMessages = async (providerId, facilityId, limit = 5) => {
  try {
    const token = await getAuthToken();
    
    const params = new URLSearchParams({
      userId: providerId,
      facilityId,
      limit: limit.toString()
    });

    const response = await fetch(`${API_BASE_URL}/internal/dashboard/messages/unread?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error fetching unread messages:', error);
    throw error;
  }
};

/**
 * Mark a message as read
 * @param {string} messageId - The message ID to mark as read
 * @param {string} userId - The user ID marking the message as read
 * @returns {Promise<Object>} Update result
 */
export const markMessageAsRead = async (messageId, userId) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/internal/messages/${messageId}/read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        read: true
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

/**
 * Delete a message
 * @param {string} messageId - The message ID to delete
 * @returns {Promise<Object>} Delete result
 */
export const deleteMessage = async (messageId) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/internal/messages/${messageId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Get message statistics for a provider
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Message statistics
 */
export const getMessageStatistics = async (providerId, facilityId) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/internal/messages/statistics?userId=${providerId}&facilityId=${facilityId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error fetching message statistics:', error);
    throw error;
  }
};

/**
 * Search messages by content or patient name
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @param {string} searchTerm - The search term
 * @param {Object} options - Search options
 * @param {number} options.limit - Maximum number of results
 * @param {string} options.before - Message ID to start after for pagination
 * @returns {Promise<Object>} Search results
 */
export const searchMessages = async (providerId, facilityId, searchTerm, options = {}) => {
  try {
    const token = await getAuthToken();
    const { limit = 50, before } = options;
    
    const params = new URLSearchParams({
      userId: providerId,
      facilityId,
      search: searchTerm,
      limit: limit.toString()
    });
    
    if (before) {
      params.append('before', before);
    }

    const response = await fetch(`${API_BASE_URL}/internal/messages/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error searching messages:', error);
    throw error;
  }
};

/**
 * Get recent conversations for a provider
 * @param {string} providerId - The provider ID (current user)
 * @param {string} facilityId - The facility ID
 * @param {number} limit - Maximum number of conversations to return
 * @returns {Promise<Object>} Recent conversations data
 */
export const getRecentConversations = async (providerId, facilityId, limit = 10) => {
  try {
    const token = await getAuthToken();
    
    const params = new URLSearchParams({
      userId: providerId,
      facilityId,
      limit: limit.toString()
    });

    const response = await fetch(`${API_BASE_URL}/internal/messages/conversations/recent?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error fetching recent conversations:', error);
    throw error;
  }
};

/**
 * Check if a patient has messaging access
 * @param {string} patientId - The patient ID to check
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Access status
 */
export const checkPatientMessagingAccess = async (patientId, facilityId) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/internal/patients/${patientId}/messaging-access?facilityId=${facilityId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return sanitizeTimestamps(data);
  } catch (error) {
    console.error('Error checking patient messaging access:', error);
    throw error;
  }
};
